/**
 * Deployment version watcher.
 *
 * Polls `/version.json` with `cache: 'no-store'` on a periodic interval and
 * whenever the browser tab regains focus or visibility. The response is
 * validated at the boundary via `BuildVersionSchema` so a gateway 502 or
 * offline HTML page is ignored rather than treated as a version change.
 *
 * When the remote `buildId` differs from the running bundle's `buildId`, the
 * supplied `onUpdateAvailable` callback runs (defaulting to a hard reload so
 * the browser fetches the latest HTML and hashed asset graph).
 */
import { env } from '@/api/config/env';
import { BuildVersionSchema } from '@/api/schemas/common';
import type { VersionLoader, VersionWatcherOptions } from '@/app/version/types';

export const VERSION_CHECK_INTERVAL_MS = 60_000;
export const VERSION_MANIFEST_PATH = '/version.json';

export const fetchRemoteBuildId: VersionLoader = async () => {
  try {
    const origin = globalThis.location?.origin ?? 'http://localhost';
    const url = new URL(VERSION_MANIFEST_PATH, origin);
    url.searchParams.set('_t', String(Date.now()));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
      cache: 'no-store',
      credentials: 'same-origin',
    });

    if (!response.ok) return null;
    const raw: unknown = await response.json();
    const parsed = BuildVersionSchema.safeParse(raw);
    return parsed.success ? parsed.data.buildId : null;
  } catch {
    return null;
  }
};

function defaultOnUpdateAvailable(): void {
  globalThis.location?.reload();
}

export function startVersionWatcher(options: VersionWatcherOptions = {}): () => void {
  const {
    currentBuildId = env.buildId,
    intervalMs = VERSION_CHECK_INTERVAL_MS,
    loadRemoteBuildId = fetchRemoteBuildId,
    onUpdateAvailable = defaultOnUpdateAvailable,
    setInterval: set = globalThis.setInterval,
    clearInterval: clear = globalThis.clearInterval,
  } = options;

  let checking = false;
  let notified = false;

  const check = async (): Promise<void> => {
    if (checking || notified) return;
    checking = true;
    try {
      const remoteBuildId = await loadRemoteBuildId();
      if (remoteBuildId && remoteBuildId !== currentBuildId) {
        notified = true;
        onUpdateAvailable(remoteBuildId);
      }
    } finally {
      checking = false;
    }
  };

  const onVisibilityOrFocus = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void check();
  };

  globalThis.addEventListener('focus', onVisibilityOrFocus, { passive: true });
  globalThis.addEventListener('visibilitychange', onVisibilityOrFocus, { passive: true });

  const handle = set(() => {
    void check();
  }, intervalMs);

  return () => {
    clear(handle);
    globalThis.removeEventListener('focus', onVisibilityOrFocus);
    globalThis.removeEventListener('visibilitychange', onVisibilityOrFocus);
  };
}
